/**
 * vehicleIconSettings.js
 *
 * Controls which map icon is shown for each vehicle based on its carMakeModel.
 *
 * HOW RULES WORK
 * ─────────────────────────────────────────────────────────────────────────────
 * • Rules run top-to-bottom; the FIRST match wins — put more-specific rules first.
 * • Matching is case-insensitive substring search by default.
 * • Vehicles that match no rule get DEFAULT_VEHICLE_ICON_TYPE ("car").
 *
 * IMPORTANT ORDERING NOTES
 * ─────────────────────────────────────────────────────────────────────────────
 * • "semi"    before "truck"   — "TRUCK-TRACTOR" contains "TRUCK", semi must win.
 * • "minibus" before "bus"     — "MINIBUS"/"MICROBUS" contain "BUS", minibus must win.
 * • Therefore the intended order is: semi → minibus → bus → truck → car.
 *
 * AVAILABLE ICON TYPES
 * ─────────────────────────────────────────────────────────────────────────────
 * "car" | "minibus" | "bus" | "truck" | "semi"
 */

export const VEHICLE_ICON_RULES = [

  // ── 1. Semi / articulated trucks ─────────────────────────────────────────
  // Must come before "truck" because "TRUCK-TRACTOR" contains "TRUCK".
  {
    type: "semi",
    keywords: [
      // Body-type descriptors (most reliable — appears in SA fleet data)
      "TRUCK-TRACTOR",
      "TRUCK TRACTOR",
      "TRACTOR UNIT",
      "ARTIC",
      "ARTICULATED",
      "SUPERLINK",
      "INTERLINK",
      "LINEHAUL",
      "LINE HAUL",
      "SEMI TRUCK",
      // OEMs almost exclusively used as artic tractor units in SA
      "ACTROS",          // Mercedes-Benz
      "FREIGHTLINER",
      "KENWORTH",
      "PETERBILT",
    ],
    // Scania/Volvo/MAN are intentionally NOT listed here — they also make buses.
    // Use the body-type keywords above (e.g. "SCANIA TRUCK-TRACTOR") or add them
    // to this array if your fleet only uses those brands as semis.
  },

  // ── 2. Minibuses, kombis & panel vans ────────────────────────────────────
  // Must come before "bus" because "MINIBUS"/"MICROBUS" contain "BUS".
  {
    type: "minibus",
    keywords: [
      // Generic
      "MINIBUS",
      "MICROBUS",
      "KOMBI",
      // Toyota
      "HIACE",
      "QUANTUM",
      // Mercedes-Benz
      "SPRINTER",
      "VITO",
      // Ford
      "TRANSIT",
      // Volkswagen
      "CRAFTER",
      // Renault / Opel / Vauxhall
      "MASTER",
      "MOVANO",
      // Fiat / Peugeot / Citroën / Iveco
      "BOXER",
      "DUCATO",
      "DAILY",
      "EXPERT",
      // Nissan
      "NV350",
      "NV200",
      // Hyundai / Kia
      "STAREX",
      "STARIA",
      "H1",
      "H100",
      "HIASE",
      // South African minibus taxi
      "SESFIKILE",
      // Foton
      "FOTON",
      // Sasuka
      "SASUKA",
      // Golden Dragon
      "GOLDEN DRAGON",
      "XML",
    ],
  },

  // ── 3. Full-size buses ────────────────────────────────────────────────────
  {
    type: "bus",
    keywords: [
      // Generic (safe now that MINIBUS/MICROBUS are already caught by rule 2)
      "BUS",
      "COACH",
      "DOUBLE DECK",
      // Toyota small bus
      "COASTER",
      "ROSA",
      // Dedicated bus brands
      "HIGER",
      "YUTONG",
      "GOLDEN DRAGON",
      "KING LONG",
      "ZHONGTONG",
      "IRIZAR",
      "MARCOPOLO",
      "NEOPLAN",
    ],
  },

  // ── 4. Trucks (rigid / medium-heavy / bakkies) ───────────────────────────
  {
    type: "truck",
    keywords: [
      // Generic
      "TRUCK",
	  "CARGO",
      // Body types
      "TIPPER",
      "DROPSIDE",
      "FLATBED",
      "FLAT BED",
      "BOXBODY",
      "BOX BODY",
      "LOADBODY",
      "SKIP",
      "MIXER",
      "TANKER",
      "CRANE",
      // Cab-over / medium-heavy rigid — common in SA
      "CANTER",          // Mitsubishi Fuso
      "NPR",             // Isuzu
      "NQR",
      "NRR",
      "FRR",
      "FTR",
      "FSR",
      "FXR",
      "FVR",
      "HINO",            // Hino 300/500/700 — mainly trucks in SA
      "UD TRUCK",
      "TATA",
      "ASHOK",
      // Pickups / bakkies (common fleet vehicles in SA)
      "HILUX",
      "RANGER",
      "NAVARA",
      "AMAROK",
      "D-MAX",
      "DMAX",
      "TRITON",
      "L200",
      "BT-50",
      "FRONTIER",
      "BAKKIE",
      "PICKUP",
    ],
  },

  // ── 5. Default: Car ───────────────────────────────────────────────────────
  // Any vehicle that does not match a rule above gets the car icon.
];

/** Fallback icon type when no rule matches */
export const DEFAULT_VEHICLE_ICON_TYPE = "car";

/**
 * Returns the icon type for a given carMakeModel string.
 * @param {string|null|undefined} carMakeModel
 * @returns {"car"|"minibus"|"bus"|"truck"|"semi"}
 */
export function getVehicleIconType(carMakeModel) {
  if (!carMakeModel) return DEFAULT_VEHICLE_ICON_TYPE;
  const upper = carMakeModel.toUpperCase();
  for (const rule of VEHICLE_ICON_RULES) {
    if (rule.keywords.some((kw) => upper.includes(kw.toUpperCase()))) {
      return rule.type;
    }
  }
  return DEFAULT_VEHICLE_ICON_TYPE;
}
